import { useMutation } from '@apollo/client'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Checkbox,
  ClickAwayListener,
  FormControl,
  FormControlLabel,
  IconButton,
  Paper,
  Popper,
  TextField,
  Typography,
} from '@mui/material'
import { Stack } from '@mui/system'
import { DatePicker, LocalizationProvider, TimePicker } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import moment from 'moment'
import React from 'react'
import { removeTypename } from '../../../common/utils'
import { i18n } from '../../index/index'
import { removeExpressCheckoutSchedule, upsertExpressCheckoutExceptionalSchedule } from '../expressCheckoutQueries'
import { Category, RestaurantCategorySchedule, RestaurantMenu, ScheduleCategory } from '../types'
import { dayNumberToWeekDay, sortCategories, sortScheduleCategories } from '../util'
interface Props {
  stationId: string
  scheduleToBeEdited?: RestaurantCategorySchedule
  scheduleToBeCopied?: RestaurantCategorySchedule
  existingSchedules?: RestaurantCategorySchedule[]
  menu: RestaurantMenu
  onCompleted?: () => void
}

function getInitialName(
  existingSchedule?: RestaurantCategorySchedule,
  scheduleToBeCopied?: RestaurantCategorySchedule,
): string {
  if (existingSchedule) {
    return existingSchedule.name
  }
  if (scheduleToBeCopied) {
    return `${scheduleToBeCopied.name} - kopio`
  }
  return ''
}

function getInitialStartDateAndEndDate(scheduleToBeEdited?: RestaurantCategorySchedule): {
  startDate: moment.Moment | null
  endDate: moment.Moment | null
} {
  if (scheduleToBeEdited) {
    return {
      startDate: scheduleToBeEdited.startDate ? moment(scheduleToBeEdited.startDate, 'YYYY-MM-DD') : null,
      endDate: scheduleToBeEdited.endDate ? moment(scheduleToBeEdited.endDate, 'YYYY-MM-DD') : null,
    }
  }
  return { startDate: null, endDate: null }
}

function getOverlappingErrorMessage(overlappingSchedules: RestaurantCategorySchedule[]): string {
  const overlappingSchedulesString = overlappingSchedules
    .map((s) => `${s.name}: ${moment(s.startDate).format('DD.MM.YYYY')}-${moment(s.endDate).format('DD.MM.YYYY')}`)
    .join(', ')
  return `${i18n.t('restaurantMenu.errorMessages.overlappingExceptionalSchedule')} (${overlappingSchedulesString})`
}

export function ExpressCheckoutScheduleForm({
  scheduleToBeEdited,
  scheduleToBeCopied,
  existingSchedules = [],
  menu,
  stationId,
  onCompleted,
}: Props) {
  const [name, setName] = React.useState<string>(getInitialName(scheduleToBeEdited, scheduleToBeCopied))
  const [selectedCategories, setSelectedCategories] = React.useState<ScheduleCategory[]>(
    removeTypename(scheduleToBeEdited?.categories ?? scheduleToBeCopied?.categories ?? []) || [],
  )
  const initialDates = getInitialStartDateAndEndDate(scheduleToBeEdited)
  const [startDate, setStartDate] = React.useState<moment.Moment | null>(initialDates.startDate)
  const [endDate, setEndDate] = React.useState<moment.Moment | null>(initialDates.endDate)
  const [popperOpen, setPopperOpen] = React.useState<{ [key: string]: HTMLElement | null }>({})
  const [upsertExceptionalScheduleMutation, upsertResult] = useMutation(upsertExpressCheckoutExceptionalSchedule, {
    onCompleted,
  })

  const [remove, removeResult] = useMutation(removeExpressCheckoutSchedule, {
    onCompleted,
  })

  const addOrRemoveCategory = (category: RestaurantMenu['categories'][0], date: moment.Moment | null) => {
    const exists = selectedCategories.find((c) => c.id === category.id)
    const openingHours = date
      ? category.defaultOpeningHours[dayNumberToWeekDay(date.day())]
      : category.defaultOpeningHours[dayNumberToWeekDay(moment().day())]
    if (exists) {
      setSelectedCategories(selectedCategories.filter((c) => c.id !== category.id))
    } else {
      setSelectedCategories([
        ...selectedCategories,
        {
          id: category.id,
          name: category.name,
          opens: openingHours.opens,
          closes: openingHours.closes,
          schedules: null,
        },
      ])
    }
  }

  const setCategoryTime = (categoryId: string, key: 'opens' | 'closes', value: moment.Moment) => {
    setSelectedCategories(
      selectedCategories.map((category) => {
        if (category.id !== categoryId) {
          return category
        }
        return {
          ...category,
          [key]: value.format('HH:mm'),
        }
      }),
    )
  }

  const setCategoryTimes = (categoryId: string, opens: moment.Moment, closes: moment.Moment) => {
    setSelectedCategories(
      selectedCategories.map((category) => {
        if (category.id !== categoryId) {
          return category
        }
        return {
          ...category,
          opens: opens.format('HH:mm'),
          closes: closes.format('HH:mm'),
          schedules: null,
        }
      }),
    )
  }

  const setCategorySchedules = (categoryId: string, schedules: { opens: string; closes: string }[] | null) => {
    setSelectedCategories(
      selectedCategories.map((category) => {
        if (category.id !== categoryId) {
          return category
        }
        return {
          ...category,
          opens: null,
          closes: null,
          schedules,
        }
      }),
    )
  }

  const toggleMultipleSchedulesForCategory = (categoryId: string) => {
    setSelectedCategories(
      selectedCategories.map((category) => {
        if (category.id !== categoryId) {
          return category
        }

        if (category.schedules) {
          // Switch from multiple to single - use first schedule or defaults
          const firstSchedule = category.schedules[0]
          return {
            ...category,
            opens: firstSchedule?.opens || '00:00',
            closes: firstSchedule?.closes || '23:59',
            schedules: null,
          }
        } else {
          // Switch from single to multiple - convert current times to first schedule
          return {
            ...category,
            opens: null,
            closes: null,
            schedules: [{ opens: category.opens || '00:00', closes: category.closes || '23:59' }],
          }
        }
      }),
    )
  }

  const getOverlappingSchedules = (): RestaurantCategorySchedule[] => {
    return existingSchedules.filter((s) => {
      const scheduleStartDate = moment(s.startDate, 'YYYY-MM-DD').startOf('day')
      const scheduleEndDate = moment(s.endDate, 'YYYY-MM-DD').endOf('day')
      return (
        startDate &&
        endDate &&
        (startDate.isBetween(scheduleStartDate, scheduleEndDate, 'days', '[]') ||
          endDate.isBetween(scheduleStartDate, scheduleEndDate, 'days', '[]') ||
          scheduleStartDate.isBetween(startDate, endDate, 'days', '[]') ||
          scheduleEndDate.isBetween(startDate, endDate, 'days', '[]'))
      )
    })
  }

  const isValid = (): boolean => {
    const overlappingSchedules = getOverlappingSchedules()
    return (
      !!name &&
      selectedCategories.length > 0 &&
      startDate !== null &&
      endDate !== null &&
      overlappingSchedules.length === 0 &&
      startDate.isSameOrBefore(endDate)
    )
  }

  const showError = (): boolean => {
    return !!startDate && !!endDate && !!name && selectedCategories.length > 0 && !isValid()
  }

  const getErrorMessages = (): string[] => {
    const errors: string[] = []
    if (endDate && startDate && endDate.isBefore(startDate)) {
      errors.push(i18n.t('restaurantMenu.errorMessages.endDateBeforeStartDate'))
    }
    const overlappingSchedules = getOverlappingSchedules()
    if (overlappingSchedules.length > 0) {
      errors.push(getOverlappingErrorMessage(overlappingSchedules))
    }
    return errors
  }

  const submit = () => {
    if (!stationId || !isValid()) {
      return
    }
    upsertExceptionalScheduleMutation({
      variables: {
        id: scheduleToBeEdited?.id,
        stationId: decodeURIComponent(stationId),
        name,
        startDate: startDate?.format('YYYY-MM-DD'),
        endDate: endDate?.format('YYYY-MM-DD'),
        categories: selectedCategories,
      },
    })
  }

  const deleteSchedule = () => {
    if (!scheduleToBeEdited?.id || !stationId) {
      return
    }
    remove({
      variables: {
        id: scheduleToBeEdited.id,
        stationId: decodeURIComponent(stationId),
      },
    })
  }

  const togglePopper = (categoryId: string, anchorEl: HTMLElement | null) => {
    if (popperOpen[categoryId]) {
      setPopperOpen({ [categoryId]: null })
      return
    }
    setPopperOpen({ [categoryId]: anchorEl })
  }

  const copyToAllCategories = (opens?: string | null, closes?: string | null) => {
    setSelectedCategories(
      selectedCategories.map((category) => ({
        ...category,
        opens,
        closes,
      })),
    )
    setPopperOpen({})
  }
  const menuCategories = sortCategories(menu.categories)

  const selectAllCategories = () => {
    const newCategories = menuCategories.filter((category) => !selectedCategories.some((c) => c.id === category.id))
    setSelectedCategories([
      ...selectedCategories,
      ...newCategories.map((c) =>
        toScheduleCategory(c, c.defaultOpeningHours.MON.opens, c.defaultOpeningHours.MON.closes),
      ),
    ])
  }

  return (
    <>
      <Paper sx={{ marginTop: '20px', padding: '10px' }}>
        <Stack spacing={2}>
          {scheduleToBeEdited !== undefined && (
            <Stack direction='row-reverse'>
              <FormControl>
                <Button variant='contained' color='error' onClick={deleteSchedule}>
                  {i18n.t('buttons.delete')}
                </Button>
              </FormControl>
            </Stack>
          )}
          <Stack direction='row'>
            <FormControl fullWidth>
              <TextField
                required
                fullWidth
                variant='outlined'
                label={i18n.t('restaurantMenu.exceptionalScheduleNameLabel')}
                placeholder={i18n.t('restaurantMenu.namePlaceholder')}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </FormControl>
          </Stack>
          <Stack direction='row' spacing={2}>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                label={i18n.t('restaurantMenu.startDateLabel')}
                onChange={(date) => setStartDate(date?.startOf('day') || null)}
                value={startDate}
              />
            </LocalizationProvider>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                label={i18n.t('restaurantMenu.endDateLabel')}
                onChange={(date) => setEndDate(date?.endOf('day') || null)}
                value={endDate}
              />
            </LocalizationProvider>
          </Stack>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>{i18n.t('restaurantMenu.selectCategoriesForSchedule')}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={1}>
                <Stack direction='row' justifyContent={'space-between'}>
                  <Typography>{i18n.t('restaurantMenu.categories')}</Typography>
                  <Button variant='outlined' onClick={() => selectAllCategories()}>
                    {i18n.t('restaurantMenu.exceptionalScheduleForm.selectAllCategories')}
                  </Button>
                </Stack>
                {menuCategories.map((category) => (
                  <FormControlLabel
                    key={category.id}
                    checked={selectedCategories.some((c) => c.id === category.id)}
                    control={<Checkbox onChange={() => addOrRemoveCategory(category, startDate)} />}
                    label={<Typography variant='body2'>{category.name}</Typography>}
                  />
                ))}
              </Stack>
            </AccordionDetails>
          </Accordion>
          <Stack spacing={2} sx={{ marginTop: '20px' }} justifyItems={'center'}>
            {sortScheduleCategories(selectedCategories).map((category) => {
              const isMultipleMode = !!(category.schedules && category.schedules.length > 0)
              return (
                <Stack key={category.id} spacing={2} direction='column'>
                  <Stack direction='row' alignItems='center' spacing={2}>
                    <Typography sx={{ minWidth: '150px' }}>{category.name}</Typography>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isMultipleMode}
                          onChange={() => toggleMultipleSchedulesForCategory(category.id)}
                        />
                      }
                      label={i18n.t('restaurantMenu.exceptionalScheduleForm.multipleOpeningHoursForCategoryLabel')}
                    />
                    {!isMultipleMode ? (
                      <>
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                          <TimePicker
                            label={i18n.t('restaurantMenu.startTimeLabel')}
                            value={moment(category.opens, 'HH:mm')}
                            onChange={(date) => date && setCategoryTime(category.id, 'opens', date)}
                          />
                          <TimePicker
                            label={i18n.t('restaurantMenu.endTimeLabel')}
                            value={moment(category.closes, 'HH:mm')}
                            onChange={(date) => date && setCategoryTime(category.id, 'closes', date)}
                          />
                        </LocalizationProvider>
                        <IconButton size='small' onClick={(e) => togglePopper(category.id, e.currentTarget)}>
                          <MoreVertIcon />
                        </IconButton>
                      </>
                    ) : (
                      <Button
                        variant='outlined'
                        size='small'
                        onClick={() => {
                          const currentSchedules = category.schedules || [{ opens: '00:00', closes: '23:59' }]
                          setCategorySchedules(category.id, [...currentSchedules, { opens: '00:00', closes: '23:59' }])
                        }}
                      >
                        {i18n.t('buttons.add')}
                      </Button>
                    )}
                  </Stack>
                  {isMultipleMode && category.schedules && (
                    <Stack spacing={1} sx={{ ml: 4 }}>
                      {category.schedules.map((schedule, index) => (
                        <Stack key={index} direction='row' spacing={2} alignItems='center'>
                          <LocalizationProvider dateAdapter={AdapterMoment}>
                            <TimePicker
                              label={`${i18n.t('restaurantMenu.startTimeLabel')} ${index + 1}`}
                              value={moment(schedule.opens, 'HH:mm')}
                              onChange={(date) => {
                                if (date && category.schedules) {
                                  const newSchedules = [...category.schedules]
                                  newSchedules[index] = { ...newSchedules[index], opens: date.format('HH:mm') }
                                  setCategorySchedules(category.id, newSchedules)
                                }
                              }}
                            />
                            <TimePicker
                              label={`${i18n.t('restaurantMenu.endTimeLabel')} ${index + 1}`}
                              value={moment(schedule.closes, 'HH:mm')}
                              onChange={(date) => {
                                if (date && category.schedules) {
                                  const newSchedules = [...category.schedules]
                                  newSchedules[index] = { ...newSchedules[index], closes: date.format('HH:mm') }
                                  setCategorySchedules(category.id, newSchedules)
                                }
                              }}
                            />
                          </LocalizationProvider>
                          {category.schedules && category.schedules.length > 1 && (
                            <Button
                              variant='text'
                              color='error'
                              size='small'
                              onClick={() => {
                                if (category.schedules) {
                                  const newSchedules = category.schedules.filter((_, i) => i !== index)
                                  setCategorySchedules(category.id, newSchedules)
                                }
                              }}
                            >
                              {i18n.t('buttons.delete')}
                            </Button>
                          )}
                        </Stack>
                      ))}
                    </Stack>
                  )}
                  {!isMultipleMode && (
                    <ClickAwayListener
                      mouseEvent='onMouseDown'
                      touchEvent='onTouchStart'
                      onClickAway={() => setPopperOpen({})}
                    >
                      <Popper
                        open={!!popperOpen[category.id]}
                        anchorEl={popperOpen[category.id]}
                        placement='bottom-end'
                      >
                        <Paper sx={{ padding: '1em' }}>
                          <Stack spacing={1} padding={1}>
                            <Button
                              variant='contained'
                              onClick={() => copyToAllCategories(category.opens, category.closes)}
                            >
                              {i18n.t('restaurantMenu.exceptionalScheduleForm.copyToOtherCategories')}
                            </Button>
                            <Button
                              variant='text'
                              onClick={() =>
                                setCategoryTimes(category.id, moment('00:00', 'HH:mm'), moment('00:00', 'HH:mm'))
                              }
                            >
                              {i18n.t('restaurantMenu.setClosedForDay')}
                            </Button>
                            <Button
                              variant='text'
                              onClick={() =>
                                setCategoryTimes(category.id, moment('00:00', 'HH:mm'), moment('23:59', 'HH:mm'))
                              }
                            >
                              {i18n.t('restaurantMenu.setAlwaysOpenForDay')}
                            </Button>
                          </Stack>
                        </Paper>
                      </Popper>
                    </ClickAwayListener>
                  )}
                </Stack>
              )
            })}
          </Stack>
        </Stack>
      </Paper>
      <Stack direction='row-reverse' sx={{ marginTop: '20px' }} spacing={2}>
        <Button variant='contained' color='primary' disabled={!isValid()} onClick={submit}>
          {i18n.t('buttons.save')}
        </Button>
        {showError() && (
          <Stack direction='column'>
            {getErrorMessages().map((errorMessage) => (
              <Typography key={errorMessage} color='error' sx={{ alignSelf: 'center', marginRight: '10px' }}>
                {errorMessage}
              </Typography>
            ))}
          </Stack>
        )}
      </Stack>
    </>
  )
}

function toScheduleCategory(category: Category, opens?: string | null, closes?: string | null): ScheduleCategory {
  return {
    id: category.id,
    name: category.name,
    opens: opens ?? '00:00',
    closes: closes ?? '23:59',
  }
}
